//path: src\features\sceneAssets\sceneAssetsActions.ts

export const SET_SCENE_ASSETS_ARRAY = 'SET_SCENE_ASSETS_ARRAY';
export const TOGGLE_LOCK = 'TOGGLE_LOCK';
export const TOGGLE_VISIBILITY = 'TOGGLE_VISIBILITY';
export const SET_SELECTED_ASSET = 'SET_SELECTED_ASSET';
export const TOGGLE_ASSET_MUTE = 'TOGGLE_ASSET_MUTE';
export const SET_CATEGORY_VISIBILITY = 'SET_CATEGORY_VISIBILITY';
export const SET_ASSET_VISIBILITY = 'SET_ASSET_VISIBILITY';
export const UPDATE_ASSET_TIMES = 'UPDATE_ASSET_TIMES';
export const ADD_SCENE_ASSET = 'ADD_SCENE_ASSET';
export const PATCH_SCENE_ASSET = 'PATCH_SCENE_ASSET';
export const REPLACE_SCENE_ASSET = 'REPLACE_SCENE_ASSET';
export const REMOVE_SCENE_ASSETS = 'REMOVE_SCENE_ASSETS';
import { AssetInstance } from '@/types/assetTypes';
// Action Types

interface SetSelectedAssetAction {
  type: typeof SET_SELECTED_ASSET;
  payload: {
    instanceId: string;
  };
}

export const setSelectedAsset = (
  instanceId: string
): SetSelectedAssetAction => ({
  type: SET_SELECTED_ASSET,
  payload: { instanceId }
});

interface ToggleLockAction {
  type: typeof TOGGLE_LOCK;
  payload: {
    assetIds: string[];
    targetLockState: boolean;
  };
}

interface ToggleVisibilityAction {
  type: typeof TOGGLE_VISIBILITY;
  payload: {
    assetIds: string[];
    targetVisibleState: boolean;
  };
}

interface ToggleMuteAction {
  type: typeof TOGGLE_ASSET_MUTE;
  payload: string | {
    assetIds: string[];
    targetMutedState?: boolean;
  };
}

interface SetSceneAssetsArrayAction {
  type: typeof SET_SCENE_ASSETS_ARRAY;
  payload: AssetInstance[];
}

interface UpdateAssetTimesAction {
  type: typeof UPDATE_ASSET_TIMES;
  payload: {
    assetInstanceId: string;
    beginTime: number;
    endTime: number;
  };
}

interface AddSceneAssetAction {
  type: typeof ADD_SCENE_ASSET;
  payload: { asset: AssetInstance; at?: number };
}

interface PatchSceneAssetAction {
  type: typeof PATCH_SCENE_ASSET;
  payload: { instanceId: string; changes: Partial<AssetInstance> };
}

interface ReplaceSceneAssetAction {
  type: typeof REPLACE_SCENE_ASSET;
  payload: { instanceId: string; asset: AssetInstance };
}

interface RemoveSceneAssetsAction {
  type: typeof REMOVE_SCENE_ASSETS;
  payload: string[];
}

export type SceneAssetsArrayActionTypes =
  | SetSceneAssetsArrayAction
  | ToggleLockAction
  | ToggleVisibilityAction
  | ToggleMuteAction
  | SetSelectedAssetAction
  | UpdateAssetTimesAction
  | AddSceneAssetAction
  | PatchSceneAssetAction
  | ReplaceSceneAssetAction
  | RemoveSceneAssetsAction

export const setSceneAssetsArray = (
  sceneAssetsArray: AssetInstance[]
): SceneAssetsArrayActionTypes => ({
  type: SET_SCENE_ASSETS_ARRAY,
  payload: sceneAssetsArray
});

/** Sets these assets' lock to the value given. */
export const toggleLock = (
  payload: { assetIds: string[], targetLockState: boolean }
): ToggleLockAction => ({
  type: TOGGLE_LOCK,
  payload
});

/** Sets these assets' visibility to the value given. */
export const toggleVisibility = (
  payload: { assetIds: string[], targetVisibleState: boolean }
): ToggleVisibilityAction => ({
  type: TOGGLE_VISIBILITY,
  payload
});

export const toggleMute = (
  payload: string | { assetIds: string[], targetMutedState?: boolean }
): ToggleMuteAction => ({
  type: TOGGLE_ASSET_MUTE,
  payload
});

export const updateAssetTimes = (
  assetInstanceId: string,
  beginTime: number,
  endTime: number
): UpdateAssetTimesAction => ({
  type: UPDATE_ASSET_TIMES,
  payload: { assetInstanceId, beginTime, endTime }
});

/** Lists an asset, at index `at` or last, unless it is listed already. */
export const addSceneAsset = (asset: AssetInstance, at?: number): AddSceneAssetAction => ({
  type: ADD_SCENE_ASSET,
  payload: { asset, at }
});

/** Changes these fields of one listed asset, leaving the rest of the list as it is now. */
export const patchSceneAsset = (instanceId: string, changes: Partial<AssetInstance>): PatchSceneAssetAction => ({
  type: PATCH_SCENE_ASSET,
  payload: { instanceId, changes }
});

/** Puts `asset` where the listed asset `instanceId` is (a swap), unless `asset` is listed already. */
export const replaceSceneAsset = (instanceId: string, asset: AssetInstance): ReplaceSceneAssetAction => ({
  type: REPLACE_SCENE_ASSET,
  payload: { instanceId, asset }
});

/** Takes these assets off the list. */
export const removeSceneAssets = (instanceIds: string[]): RemoveSceneAssetsAction => ({
  type: REMOVE_SCENE_ASSETS,
  payload: instanceIds
});
